// src/pages/AccountSettings.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import './style.css';
import { CloseIcon } from '../../assets/icons/icons';
import LoadingScreen from '../LoadingScreen/LoadingScreen';

export default function Account() {
  const tabs = [
    'Profile',
    'Billing',
    'Team',
    // TODO 'Umbrella'
  ];
  const [activeTab, setActiveTab] = useState('Profile');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [trial, setTrial] = useState(false);

  const handleLogout = async () => {
        await supabase.auth.signOut();
        setIsAuthenticated(false);
        window.location.href = '/'; // Redirect to home after logout
  };

  // store current user ID so we can update profile
  const [userId, setUserId] = useState(null);

  // PROFILE
  const [username, setUsername]     = useState('');
  const [email, setEmail]           = useState('');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwdForm, setShowPwdForm] = useState(false);

  // BILLING
  const [subscription, setSubscription]     = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);

  // TEAM
  const [team, setTeam]                     = useState([]);
  const [inviteEmail, setInviteEmail]       = useState('');
  const [inviteNickname, setInviteNickname] = useState('');

  // UMBRELLA
  const [umbrella, setUmbrella] = useState(null);

  // GLOBAL
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data: { user }, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw userErr;
        setUserId(user.id);

        // PROFILE
        setEmail(user.email || '');
        const { data: profile, error: profErr } = await supabase
          .from('users')
          .select('username, trial')
          .eq('id', user.id)
          .single();
        if (!profErr) setUsername(profile.username || '');
        console.log('profile', profile);
        setTrial(!!profile.trial);

        // if trial, we skip loading the rest
        if (profile.trial) {
          return 
        }

        // BILLING – grab the plan info in the same call
        let { data: subData, error: subErr } = await supabase
        .from('subscriptions')
        .select(`
            id,
            athlete_count,
            plan_id,
            subscription_plans (
            name,
            per_athlete_cents
            )
        `)
        .eq('user_id', user.id)
        .single();
        if (subErr) {
           console.error('failed to load subscription', subErr);
        } else if (subData) {
          const plan = subData.subscription_plans;
          setSubscription({
              id: subData.id,
              plan_name: plan.name,
              // format $x.xx
              price_fmt: `$${(plan.per_athlete_cents/100).toFixed(2)}`,
              interval: 'per month',
              athlete_count: subData.athlete_count,
          });
        }

        const { data: invoices, error: invErr } = await supabase
          .from('invoices')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });
        if (!invErr) setBillingHistory(invoices || []);

        // TEAM
        const { data: members, error: teamErr } = await supabase
          .from('team_members')
          .select('*')
          .eq('owner_id', user.id)
          .order('created_at', { ascending: true });
        if (!teamErr) setTeam(members || []);

        // TODO UMBRELLA
        // const { data: umb, error: umbErr } = await supabase
        //   .from('umbrella_accounts')
        //   .select('name,email')
        //   .eq('user_id', user.id)
        //   .maybeSingle();
        // if (!umbErr) setUmbrella(umb || null);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load account data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // — PROFILE: update username —
  const saveUsername = async () => {
    if (!username.trim()) {
      return alert('Username cannot be empty.');
    }
    const { error } = await supabase
      .from('users')
      .update({ username: username.trim() })
      .eq('id', userId);
    if (error) {
      alert('Failed to update account name.');
      console.error(error);
    } else {
      alert('Account name updated.');
    }
  };

  // PROFILE → Update Password
  const savePassword = async () => {
    if (newPwd !== confirmPwd) {
      return alert('New passwords must match');
    }
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: newPwd });
      if (updErr) throw updErr;
      alert('Password updated');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); setShowPwdForm(false);
    } catch {
      alert('Error updating password');
    }
  };

  // TEAM → Invite / Toggle / Remove
  const inviteMember = async () => {
    if (!inviteEmail) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error: invErr } = await supabase
        .from('team_members')
        .insert({
          owner_id: user.id,
          member_email: inviteEmail,
          nickname: inviteNickname,
          is_active: true
        })
        .select()
        .single();
      if (invErr) throw invErr;
      setTeam(t => [...t, data]);
      setInviteEmail(''); setInviteNickname('');
    } catch {
      alert('Invite failed');
    }
  };
  const toggleActive = async (id, active) => {
    await supabase
      .from('team_members')
      .update({ is_active: !active })
      .eq('id', id);
    setTeam(t => t.map(m => m.id===id ? { ...m, is_active: !active } : m));
  };
  const removeMember = async id => {
    await supabase.from('team_members').delete().eq('id', id);
    setTeam(t => t.filter(m => m.id!==id));
  };

  const formatCents = cents => {
    const n = Number(cents) || 0;
    return (n / 100).toLocaleString('en-US', {
    style:    'currency',
    currency: 'USD',
    });
  };

  if (loading) return <LoadingScreen targetPath="/account" />;
  if (error)   return <p className="error">{error}</p>;
  if (trial) {
    return (
      <div className='account-settings'>
      <div className="my-goals-container">
        <div className='write-content'>
          <h2 className='my-goals-title'>Account Settings</h2>
          <p>Exclusive to Full Members</p>
          <button className="add-goal-btn" onClick={() => window.location.href = '/purchase-subscription'}>
            Register Now
          </button>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <p>Want to <strong>logout?</strong></p>
          <button className="cancel-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="account-settings">
      <h2>Account Settings</h2>
      <div className="tabs">
        {tabs.map(t => (
          <button
            key={t}
            className={t===activeTab ? 'active' : ''}
            onClick={()=>setActiveTab(t)}
          >{t}</button>
        ))}
      </div>

      {/* — PROFILE — */}
      {activeTab==='Profile' && (
        <>
        <section>
            <div className="card">
                <h2>Profile</h2>
                <label className='account-label'>Account Name</label>
                <input value={username} onChange={e=>setUsername(e.target.value)} />
                <button className="active" onClick={saveUsername}>
                  Set Account Name
                </button>
                <label className='account-label'>Email</label>
                <input value={email} readOnly />
            </div>
            <div className='card'>
                <h2 className='account-title'>Password</h2>
                <p className='plan-price'>Change your current password</p>
                <label>Current Password</label>
                <input
                    type="password"
                    value={currentPwd}
                    onChange={e=>setCurrentPwd(e.target.value)}
                />

                <label>New Password</label>
                <input
                type="password"
                value={newPwd}
                onChange={e=>setNewPwd(e.target.value)}
                />
                <label>Confirm New Password</label>
                <input
                type="password"
                value={confirmPwd}
                onChange={e=>setConfirmPwd(e.target.value)}
                />
                <button className="active" onClick={savePassword}>
                Update Password
                </button>
              </div>
              <div className='card'>
                <p>Want to <strong>logout?</strong></p>
                <button className="cancel-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
        </section>
        </>
      )}

      {/* — BILLING — */}
      {activeTab==='Billing' && (
        <section>
        <div className="card">
          <h2 className='account-title'>Subscription Plan</h2>
          <p className='plan-price'>Manage your subscription and billing</p>
          <br></br>
          {subscription ? (
            <>
                <div className="plan-card">
                    <div className='plan-card-header'>
                      <div>
                      <h3 className='plan-title'>SWP Plan</h3>
                      <p className='plan-price'>
                          {subscription.price_fmt} per athlete,  {subscription.interval}
                      </p>
                      </div>
                      <div className='plan-btns'>
                        <span className='athlete-count-bubble'>Athlete Count:  <strong>{subscription.athlete_count}</strong></span>
                      </div>
                    </div>
                    <div className='account-divider'></div>
                    <p className='plan-details'>
                        <span className='account-green-check'>✓</span> {subscription.plan_name}
                    </p>
                    <p className='plan-details'>
                        <span className='account-green-check'>✓</span> Unlimited evaluations
                    </p>
                    <p className='plan-details'>
                        <span className='account-green-check'>✓</span> 3 team members
                    </p>
                </div>
                <p>
                    To <strong>cancel</strong> or <strong>change</strong> subscription, please contact{' '}
                    <a className='sub-mail-link' href="mailto:info@lassosafe.com">info@lassosafe.com</a>
                </p>
            </>
    ) : (
      <>
        <p>Ready to purchase the SWP?</p>
        <button className="add-goal-btn" onClick={() => window.location.href = '/purchase-subscription'}>
          Purchase Subscription 
        </button>
      </>
    )}
    </div>
        <div className='card'>
          <h2 className='account-title'>Billing History</h2>
          <p className='plan-price'>View your past invoices and payments</p>
          {billingHistory.length > 0 ? (
            <table className="billing-history">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Frequency</th>
                  <th>Amount</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map(inv => (
                  <tr key={inv.id}>
                    <td>{new Date(inv.date).toLocaleDateString()}</td>
                    <td>{inv.description}</td>
                    <td>{inv.frequency}</td>
                    <td>{formatCents(inv.amount_cents)}</td>
                    <td>
                      <a href={inv.invoice_url} className='invoice-link' target="_blank" rel="noreferrer">
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No invoices yet.</p>
          )}
        </div>
        </section>
      )}

      {/* — TEAM — */}
      {activeTab==='Team' && (
        <section className="card">
          <h2>Team Members</h2>
          <div className="invite-row">
            <input
              type="email"
              placeholder="Email to invite"
              value={inviteEmail}
              onChange={e=>setInviteEmail(e.target.value)}
            />
            <input
              placeholder="Nickname"
              value={inviteNickname}
              onChange={e=>setInviteNickname(e.target.value)}
            />
            <button className="member-btn" onClick={inviteMember}>Invite</button>
          </div>
          {team.length > 0 ? (
            <table className="team-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nickname</th>
                  <th>Active</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {team.map(m => (
                  <tr key={m.id}>
                    <td>{m.member_email}</td>
                    <td>{m.nickname}</td>
                    <td>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={m.is_active}
                          onChange={() => toggleActive(m.id, m.is_active)}
                        />
                        <span className="slider" />
                      </label>
                    </td>
                    <td>
                      {/* <button onClick={()=>removeMember(m.id)}>Remove</button> */}
                      <CloseIcon className='remove-icon' onClick={()=>removeMember(m.id)}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No team members yet.</p>
          )}
        </section>
      )}

      {/* — UMBRELLA — */}
      {activeTab==='Umbrella' && (
        <section className="card">
          <h2>Umbrella Account</h2>
          {umbrella ? (
            <>
              <p><strong>Name:</strong> {umbrella.name}</p>
              <p><strong>Email:</strong> {umbrella.email}</p>
            </>
          ) : (
            <p>You have not yet added an Umbrella account.</p>
          )}
        </section>
      )}
    </div>
  );
}
